import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  input,
  output,
  signal
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule
} from '@angular/forms';
import { DatePickerModule } from 'primeng/datepicker';
import { FloatLabelModule } from 'primeng/floatlabel';

/**
 * Componente que renderiza un input de tipo calendario
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-input-date',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DatePickerModule,
    FloatLabelModule
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppInputDateComponent),
      multi: true
    }
  ],
  template: `
    @if (staticLabel()) {
      <div class="flex flex-col gap-1">
        <label class="text-sm font-normal text-blue-300" for="{{ id() }}">{{
          label()
        }}</label>
        <p-datepicker
          [inline]="inline()"
          [inputId]="id()"
          [showIcon]="showIcon()"
          [showOnFocus]="showOnFocus()"
          [showButtonBar]="true"
          [keepInvalid]="true"
          [minDate]="minDate()"
          [maxDate]="maxDate()"
          [disabled]="isDisabled()"
          dateFormat="yy-mm-dd"
          styleClass="w-full !border-blue-deep hover:!border-blue-deep focus:!border-blue-deep !text-blue-deep"
          appendTo="body"
          (onChange)="onDateChange()"
          [(ngModel)]="value"
          (ngModelChange)="updateValue($event)"
        />
      </div>
    } @else {
      <p-floatLabel>
        <p-datepicker
          [inline]="inline()"
          [inputId]="id()"
          [showIcon]="showIcon()"
          [showOnFocus]="showOnFocus()"
          [showButtonBar]="true"
          [keepInvalid]="true"
          [minDate]="minDate()"
          [maxDate]="maxDate()"
          [disabled]="isDisabled()"
          dateFormat="yy-mm-dd"
          styleClass="w-full !border-blue-deep hover:!border-blue-deep focus:!border-blue-deep !text-blue-deep"
          appendTo="body"
          (onChange)="onDateChange()"
          [(ngModel)]="value"
          (ngModelChange)="updateValue($event)"
        />
        <label class="!text-blue-deep" for="{{ id() }}">{{ label() }}</label>
      </p-floatLabel>
    }
  `,
  styles: ``
})
export class AppInputDateComponent implements ControlValueAccessor {
  /**
   * Identificador del input
   */
  id = input.required<string>();

  /**
   * Etiqueta del input
   */
  label = input.required<string>();

  /**
   * Fecha mínima que es seleccionable
   */
  minDate = input<Date>();

  /**
   * Fecha máxima que es seleccionable
   */
  maxDate = input<Date>();

  /**
   * Mostrar calendario completo con selector de flechas
   */
  inline = input<boolean>();

  /**
   * Mostrar icono del calendario
   */
  showIcon = input<boolean>(true);

  /**
   * Abrir calendario al hacer foco en el input
   */
  showOnFocus = input<boolean>(false);

  /**
   * Usar label estático sin animación flotante
   */
  staticLabel = input<boolean>(false);

  /**
   * Evento que se emite cuando el valor del input cambia
   */
  changed = output<Date | undefined>();

  /**
   * Valor del input
   */
  value = signal<Date | undefined>(undefined);

  /**
   * Indica si el input está deshabilitado
   */
  isDisabled = signal(false);

  onChange?: (value: Date) => void;

  onTouched?: () => void;

  writeValue(value: Date): void {
    this.value.set(value);
  }

  registerOnChange(fn: (value: Date) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  updateValue(event: Date): void {
    this.value.set(event);
    this.onChange?.(event);
    this.onTouched?.();
    this.changed.emit(event);
  }

  onDateChange(): void {
    this.changed.emit(this.value());
  }
}
